import type {
  RightToInformationGetStep1,
  RightToInformationGetStep2,
  RightToInformationGetStep3,
  RightToInformationGetStep4,
  RightToInformationSaveStep1,
  RightToInformationSaveStep2,
  RightToInformationSaveStep4,
  RightToInformationStep4File,
  RightToInformationStepsResponse,
  SaveRightToInformationStepsPayload,
} from '../types/rightToInformationApi';
import type { RightToInformationWizardDraft } from '../types/rightToInformationCaseRequest';
import { isDocumentTypeUuid, isPredefinedDocumentType, resolveDocumentTypeUuid } from '../utils/documentTypeId';
import { caseRequestIdForSave, draftResumeStepFromApi, finalizeCaseRequestSavePayload, SaveStepFields, strOrNull, apiStepString } from '../utils/caseRequestPayload';
import { looksLikeStorageKey, normalizeDocumentFileFields } from '../utils/fileDisplayName';
import type { RightToInformationWizardStep } from '../pages/wizard/rightToInformation/rightToInformationConstants';
import {
  FILE_CATEGORY_RIGHT_TO_INFORMATION,
  RIGHT_TO_INFORMATION_CATEGORY_ID,
} from '../pages/wizard/rightToInformation/rightToInformationConstants';
import { parseApplicableAreasFromApi } from './criminalCaseMapper';

export type { RightToInformationWizardStep };

export function caseRequestIdFromResponse(
  response: Pick<RightToInformationStepsResponse, 'case_request_id' | 'caseRequestId'>,
): string {
  const id = response.case_request_id ?? response.caseRequestId;
  return typeof id === 'string' ? id.trim() : '';
}

export function referenceNoFromResponse(response: RightToInformationStepsResponse): string | undefined {
  const ref = response.reference_no ?? response.referenceNo ?? response.fileReferenceNumber;
  const t = typeof ref === 'string' ? ref.trim() : '';
  return t || undefined;
}

function stepProcessed(
  response: RightToInformationStepsResponse,
  camel: keyof RightToInformationStepsResponse,
  snake: keyof RightToInformationStepsResponse,
): boolean | undefined {
  const c = response[camel];
  if (typeof c === 'boolean') return c;
  const s = response[snake];
  if (typeof s === 'boolean') return s;
  return undefined;
}

function step1LooksComplete(s1: RightToInformationGetStep1 | undefined, response: RightToInformationStepsResponse): boolean {
  const subId = (response.file_sub_category_id ?? '').trim();
  const requestor = (s1?.requestor_name ?? '').trim();
  const details = (s1?.requested_information_details ?? '').trim();
  return Boolean(subId && requestor && details);
}

function step2LooksComplete(s2: RightToInformationGetStep2 | undefined): boolean {
  return Boolean((s2?.urgency_level_id ?? '').trim());
}

/** UI step 3 (Submission) is read-only; CMS-CRRTI-1 save has no step_3 payload. */
function step3LooksComplete(): boolean {
  return true;
}

function step4LooksComplete(s4: RightToInformationGetStep4 | undefined): boolean {
  const files = step4DocumentsFromApi(s4);
  return files.some(
    (f) =>
      Boolean((f.file_name ?? '').trim() || (f.original_file_name ?? '').trim()) ||
      Boolean((f.reason ?? '').trim()),
  );
}

export function resolveRightToInformationDraftResumeStep(response: RightToInformationStepsResponse): RightToInformationWizardStep {
  return draftResumeStepFromApi(
    response,
    () => {
  if (stepProcessed(response, 'step1Processed', 'step_1_processed') === false) return 1;
  if (stepProcessed(response, 'step2Processed', 'step_2_processed') === false) return 2;
  if (stepProcessed(response, 'step3Processed', 'step_3_processed') === false) return 3;
  if (stepProcessed(response, 'step4Processed', 'step_4_processed') === false) return 4;

  const s1 = response.step_1;
  const s2 = response.step_2;
  const s4 = response.step_4;

  if (!step1LooksComplete(s1, response)) return 1;
  if (!step2LooksComplete(s2)) return 2;
  if (!step3LooksComplete()) return 3;
  if (!step4LooksComplete(s4)) return 4;
  return 4;
    },
    (step) => step >= 1 && step <= 4,
  );
}

function step4DocumentsFromApi(s4: RightToInformationGetStep4 | undefined): RightToInformationStep4File[] {
  return [
    ...(s4?.uploaded_files ?? []),
    ...(s4?.extra_uploaded_files ?? []),
    ...(s4?.documents ?? []),
  ];
}

function preferredAccessIdsFromStep1(s1: RightToInformationGetStep1 | undefined): string[] {
  if (!s1?.preferred_method_of_access_ids?.length) return [];
  return uuidLookupIds(
    s1.preferred_method_of_access_ids.filter((id) => typeof id === 'string' && id.trim()),
  );
}

function preferredAccessNamesFromStep1(s1: RightToInformationGetStep1 | undefined): string[] {
  if (!s1?.preferred_method_of_access_names?.length) return [];
  return s1.preferred_method_of_access_names
    .map((name) => (typeof name === 'string' ? name.trim() : ''))
    .filter(Boolean);
}

function apiDocumentTypeId(file: RightToInformationStep4File): string {
  return (file.document_type_id ?? file.document_id ?? '').trim();
}

function yesNoToBool(v: '' | 'yes' | 'no'): boolean | undefined {
  if (v === 'yes') return true;
  if (v === 'no') return false;
  return undefined;
}

function boolToYesNo(v: boolean | undefined | null): '' | 'yes' | 'no' {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return '';
}

function uuidLookupIds(ids: string[]): string[] {
  return ids.filter((id) => isDocumentTypeUuid(id));
}

type SavedFileRow = {
  file_name?: string | null;
  original_file_name?: string | null;
  fileName?: string | null;
  originalFileName?: string | null;
};

function savedFileFields(file: SavedFileRow): { fileName: string; originalFileName: string } {
  const apiFileName = file.file_name ?? file.fileName ?? '';
  const original = file.original_file_name ?? file.originalFileName;
  return normalizeDocumentFileFields(apiFileName, original);
}

function step4FileFields(file: RightToInformationStep4File): { fileName: string; originalFileName: string } {
  return savedFileFields(file);
}

function step4FileNamesForSave(
  fileName: string | null | undefined,
  originalFileName: string | null | undefined,
): { file_name?: string; original_file_name?: string } {
  const a = fileName?.trim() ?? '';
  const b = originalFileName?.trim() ?? '';
  if (!a && !b) return {};

  if (a && looksLikeStorageKey(a)) {
    return { file_name: a, ...(b ? { original_file_name: b } : {}) };
  }
  if (b && looksLikeStorageKey(b)) {
    return { file_name: b, ...(a ? { original_file_name: a } : {}) };
  }

  return { file_name: a, ...(b ? { original_file_name: b } : {}) };
}

function resolveApplicableAreaIds(response: RightToInformationStepsResponse, prev: string[]): string[] {
  for (const src of [response.applicable_areas]) {
    const { ids } = parseApplicableAreasFromApi(src);
    if (ids.length) return ids;
  }
  if (Array.isArray(response.applicableAreaIds) && response.applicableAreaIds.length) {
    return response.applicableAreaIds.filter((id) => typeof id === 'string' && id.trim());
  }
  return prev;
}

function resolveApplicableAreaNames(response: RightToInformationStepsResponse, prev: string[]): string[] {
  for (const src of [response.applicable_areas]) {
    const { names } = parseApplicableAreasFromApi(src);
    if (names.length) return names;
  }
  const named = response.applicable_area_names ?? response.applicableAreaNames;
  if (Array.isArray(named) && named.length) {
    return named.filter((n): n is string => typeof n === 'string' && Boolean(n.trim()));
  }
  return prev;
}

function mapStep1(draft: RightToInformationWizardDraft): SaveStepFields<RightToInformationSaveStep1> {
  const accessIds = uuidLookupIds(draft.preferredMethodOfAccessIds);
  const preferredLanguageId = strOrNull(draft.preferredLanguageId);
  const step1: SaveStepFields<RightToInformationSaveStep1> = {
    requestor_name: strOrNull(draft.requestorName),
    nic_or_passport_number: strOrNull(draft.nicOrPassportNumber),
    address: strOrNull(draft.address),
    contact_number: strOrNull(draft.contactNumber),
    email_address: strOrNull(draft.emailAddress),
    is_sri_lankan_citizen: yesNoToBool(draft.isSriLankanCitizen),
    specific_public_authority_name: strOrNull(draft.specificPublicAuthorityName),
    requested_information_details: strOrNull(draft.requestedInformationDetails),
    information_requested_time_period: strOrNull(draft.informationRequestedTimePeriod),
    involves_life_or_personal_liberty: yesNoToBool(draft.involvesLifeOrPersonalLiberty),
    additional_notes: strOrNull(draft.additionalNotes),
    preferred_method_of_access_ids: accessIds.length ? accessIds : undefined,
    preferred_language_id:
      preferredLanguageId && isDocumentTypeUuid(preferredLanguageId) ? preferredLanguageId : null,
    justification: strOrNull(draft.justification),
  };

  return step1;
}

function mapStep2(draft: RightToInformationWizardDraft): SaveStepFields<RightToInformationSaveStep2> {
  return {
    urgency_level_id: strOrNull(draft.urgencyLevelId),
    deadline: strOrNull(draft.deadline),
    urgency_justification: strOrNull(draft.urgencyJustification),
  };
}

function mapStep4(draft: RightToInformationWizardDraft): SaveStepFields<RightToInformationSaveStep4> {
  const uploaded_files = draft.documents
    .filter(
      (d) =>
        isPredefinedDocumentType(d.documentTypeId, d.checklistItemId) && (strOrNull(d.fileName) || strOrNull(d.skipReason)),
    )
    .map((d) => {
      const reason = strOrNull(d.skipReason);
      const names = step4FileNamesForSave(strOrNull(d.fileName), strOrNull(d.originalFileName));
      const documentTypeId = resolveDocumentTypeUuid(d.documentTypeId, d.checklistItemId);
      return {
        ...(documentTypeId ? { document_type_id: documentTypeId } : {}),
        ...names,
        ...(reason ? { reason } : {}),
      };
    });
  const extra_uploaded_files = draft.documents
    .filter(
      (d) =>
        !isPredefinedDocumentType(d.documentTypeId, d.checklistItemId) && (strOrNull(d.fileName) || strOrNull(d.skipReason)),
    )
    .map((d) => {
      const reason = strOrNull(d.skipReason);
      const names = step4FileNamesForSave(strOrNull(d.fileName), strOrNull(d.originalFileName));
      return {
        ...names,
        ...(reason ? { reason } : {}),
      };
    });

  return {
    ...(uploaded_files.length ? { uploaded_files } : {}),
    ...(extra_uploaded_files.length ? { extra_uploaded_files } : {}),
  };
}

function applyStep1And2(payload: SaveRightToInformationStepsPayload, draft: RightToInformationWizardDraft): void {
  payload.step_1 = mapStep1(draft);
  payload.step_2 = mapStep2(draft);
}

function categoryIdForSave(draft: RightToInformationWizardDraft): string | undefined {
  return strOrNull(draft.fileCategoryId) || strOrNull(RIGHT_TO_INFORMATION_CATEGORY_ID) || undefined;
}

/** CMS-CRRTI-1 — save payload: step_1, step_2, step_4 only (no step_3). */
export function buildSaveRightToInformationPayload(
  draft: RightToInformationWizardDraft,
  options: {
    currentStep?: RightToInformationWizardStep;
    isDraft: boolean;
    confirmedCorrect?: boolean;
    allSteps?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): SaveRightToInformationStepsPayload {
  const areaIds = draft.applicableAreaIds.filter(Boolean);
  const subCategoryId = strOrNull(draft.subFileCategoryId);
  const payload: SaveRightToInformationStepsPayload = {
    is_draft_status: options.isDraft,
  };
  const caseRequestId = caseRequestIdForSave(draft.draftRequestId);
  if (caseRequestId) payload.case_request_id = caseRequestId;

  const categoryId = categoryIdForSave(draft);
  if (categoryId) payload.category_id = categoryId;

  if (subCategoryId) payload.file_sub_category_id = subCategoryId;
  if (options.isUpdateAlreadySubmitted) payload.is_update_already_submitted = true;
  if (options.confirmedCorrect != null) payload.confirmed_correct = options.confirmedCorrect;
  if (caseRequestId || areaIds.length) payload.applicable_areas = areaIds.length ? areaIds : null;

  if (options.allSteps) {
    applyStep1And2(payload, draft);
    payload.step_4 = mapStep4(draft);
    return finalizeCaseRequestSavePayload(payload, options);
  }

  const step = options.currentStep;
  if (!step) return finalizeCaseRequestSavePayload(payload, options);

  if (options.isDraft) {
    if (step >= 1) payload.step_1 = mapStep1(draft);
    if (step >= 2) payload.step_2 = mapStep2(draft);
    if (step >= 4) payload.step_4 = mapStep4(draft);
    return finalizeCaseRequestSavePayload(payload, options);
  }

  if (step === 1) payload.step_1 = mapStep1(draft);
  if (step === 2) payload.step_2 = mapStep2(draft);
  if (step === 3) applyStep1And2(payload, draft);
  if (step === 4) payload.step_4 = mapStep4(draft);

  return finalizeCaseRequestSavePayload(payload, options);
}

function mapGetStep3ToDraft(
  s3: RightToInformationGetStep3 | undefined,
  prev: RightToInformationWizardDraft,
): Pick<
  RightToInformationWizardDraft,
  | 'submittedByUserId'
  | 'submittedByDisplayName'
  | 'submittedAgencyName'
  | 'submittedDivisionName'
  | 'submitterDesignation'
  | 'submitterEmail'
  | 'submitterContactNo'
> {
  if (!s3) {
    return {
      submittedByUserId: prev.submittedByUserId,
      submittedByDisplayName: prev.submittedByDisplayName,
      submittedAgencyName: prev.submittedAgencyName,
      submittedDivisionName: prev.submittedDivisionName,
      submitterDesignation: prev.submitterDesignation,
      submitterEmail: prev.submitterEmail,
      submitterContactNo: prev.submitterContactNo,
    };
  }

  return {
    submittedByUserId: apiStepString(true, s3.submitted_by_user_id, prev.submittedByUserId),
    submittedByDisplayName: apiStepString(true, s3.submitted_by_display_name, prev.submittedByDisplayName),
    submittedAgencyName: apiStepString(true, s3.submitted_agency_name, prev.submittedAgencyName),
    submittedDivisionName: apiStepString(true, s3.submitted_division_name, prev.submittedDivisionName),
    submitterDesignation: apiStepString(true, s3.designation, prev.submitterDesignation),
    submitterEmail: apiStepString(true, s3.email_address ?? s3.email, prev.submitterEmail),
    submitterContactNo: apiStepString(true, s3.contact_number ?? s3.contact_no, prev.submitterContactNo),
  };
}

export function mapRightToInformationStepsToDraft(
  response: RightToInformationStepsResponse,
  prev: RightToInformationWizardDraft,
): RightToInformationWizardDraft {
  const s1 = response.step_1;
  const s2 = response.step_2;
  const s3 = response.step_3;
  const s4 = response.step_4;
  const hasStep1 = s1 != null;
  const hasStep2 = s2 != null;

  const documents = (() => {
    const fromApi = step4DocumentsFromApi(s4);
    if (!fromApi.length) return prev.documents;
    if (!prev.documents.length) {
      return fromApi
        .filter((f) => {
          const typeId = apiDocumentTypeId(f);
          return typeId
            ? isDocumentTypeUuid(typeId)
            : Boolean((f.file_name ?? '').trim() || (f.reason ?? '').trim());
        })
        .map((f) => {
          const { fileName, originalFileName } = step4FileFields(f);
          const typeId = apiDocumentTypeId(f);
          return {
            id: (f.case_request_document_id ?? '').trim() || crypto.randomUUID(),
            checklistItemId: '',
            documentTypeId: typeId,
            documentName: 'Document',
            mandatory: false,
            fileName,
            originalFileName,
            uploadTime: f.upload_time ?? '',
            skipReason: f.reason ?? '',
          };
        });
    }
    return prev.documents.map((doc) => {
      const match = fromApi.find((f) => {
        const typeId = apiDocumentTypeId(f);
        if (typeId && typeId === doc.documentTypeId) return true;
        return false;
      });
      if (!match) return doc;
      const { fileName, originalFileName } = step4FileFields(match);
      return {
        ...doc,
        documentTypeId: apiDocumentTypeId(match) || doc.documentTypeId,
        fileName: fileName || doc.fileName,
        originalFileName: originalFileName || doc.originalFileName,
        uploadTime: match.upload_time ?? doc.uploadTime,
        skipReason: match.reason ?? doc.skipReason,
      };
    });
  })();

  const fileCategoryId =
    (response.file_category_id ?? prev.fileCategoryId) || RIGHT_TO_INFORMATION_CATEGORY_ID;
  const fileCategoryName =
    (response.file_category_name ?? prev.fileCategory) || FILE_CATEGORY_RIGHT_TO_INFORMATION;
  const subFileCategoryId =
    response.file_sub_category_id ?? prev.subFileCategoryId;
  const subFileCategoryName =
    response.file_sub_category_name ?? prev.subFileCategoryName;

  const preferredAccessIds = hasStep1 ? preferredAccessIdsFromStep1(s1) : prev.preferredMethodOfAccessIds;

  return {
    ...prev,
    ...mapGetStep3ToDraft(s3, prev),
    draftRequestId: caseRequestIdFromResponse(response) || prev.draftRequestId,
    referenceNo: referenceNoFromResponse(response) ?? prev.referenceNo,
    fileCategory: fileCategoryName,
    fileCategoryId,
    subFileCategoryId,
    subFileCategoryName,
    applicableAreaIds: resolveApplicableAreaIds(response, prev.applicableAreaIds),
    applicableAreas: resolveApplicableAreaNames(response, prev.applicableAreas),
    requestorName: apiStepString(hasStep1, s1?.requestor_name, prev.requestorName),
    nicOrPassportNumber: apiStepString(hasStep1, s1?.nic_or_passport_number, prev.nicOrPassportNumber),
    address: apiStepString(hasStep1, s1?.address, prev.address),
    contactNumber: apiStepString(hasStep1, s1?.contact_number, prev.contactNumber),
    emailAddress: apiStepString(hasStep1, s1?.email_address, prev.emailAddress),
    isSriLankanCitizen: hasStep1
      ? boolToYesNo(s1?.is_sri_lankan_citizen) || 'no'
      : prev.isSriLankanCitizen || 'no',
    specificPublicAuthorityName: apiStepString(
      hasStep1,
      s1?.specific_public_authority_name,
      prev.specificPublicAuthorityName,
    ),
    requestedInformationDetails: apiStepString(
      hasStep1,
      s1?.requested_information_details,
      prev.requestedInformationDetails,
    ),
    informationRequestedTimePeriod: apiStepString(
      hasStep1,
      s1?.information_requested_time_period,
      prev.informationRequestedTimePeriod,
    ),
    preferredMethodOfAccessIds: preferredAccessIds,
    preferredMethodOfAccessNames: hasStep1
      ? preferredAccessNamesFromStep1(s1)
      : prev.preferredMethodOfAccessNames,
    urgencyLevelId: apiStepString(hasStep2, s2?.urgency_level_id, prev.urgencyLevelId),
    urgencyLevelName: apiStepString(hasStep2, s2?.urgency_level_name, prev.urgencyLevelName),
    deadline: apiStepString(hasStep2, s2?.deadline, prev.deadline),
    urgencyJustification: apiStepString(hasStep2, s2?.urgency_justification, prev.urgencyJustification),
    preferredLanguageId: apiStepString(hasStep1, s1?.preferred_language_id, prev.preferredLanguageId),
    preferredLanguageName: apiStepString(hasStep1, s1?.preferred_language_name, prev.preferredLanguageName),
    involvesLifeOrPersonalLiberty: hasStep1
      ? boolToYesNo(s1?.involves_life_or_personal_liberty) || 'no'
      : prev.involvesLifeOrPersonalLiberty || 'no',
    justification: apiStepString(hasStep1, s1?.justification, prev.justification),
    additionalNotes: apiStepString(hasStep1, s1?.additional_notes, prev.additionalNotes),
    documents,
  };
}
